import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Avatar,
  Box,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Help as HelpIcon
} from '@mui/icons-material';

const TopAppBar = ({ user, isMobile, isTablet, onSearch, hideTitle = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [searchValue, setSearchValue] = useState('');

  // Handle menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle notifications menu open
  const handleNotificationsOpen = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  // Handle notifications menu close
  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    onSearch(e.target.value);
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        backgroundColor: 'rgba(26, 32, 44, 0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 10
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        {/* Left Section - Logo/Title */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {!hideTitle && (
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 'bold',
                background: 'linear-gradient(90deg, #4C85F0, #9370DB)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                display: { xs: 'none', sm: 'block' },
                mr: 2
              }}
            >
              SERVICE DESK
            </Typography>
          )}
        </Box>

        {/* Middle Section - Search */}
        <Box
          sx={{
            flex: 1,
            mx: 2,
            maxWidth: '600px',
            display: { xs: 'none', md: 'block' }
          }}
        >
          <TextField
            fullWidth
            placeholder="Search tickets, customers, or messages..."
            size="small"
            value={searchValue}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'transparent'
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.3)'
                }
              }
            }}
          />
        </Box>

        {/* Right Section - Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Help Button */}
          <Tooltip title="Help & Resources">
            <IconButton color="inherit" sx={{ ml: 1 }}>
              <HelpIcon />
            </IconButton>
          </Tooltip>

          {/* Settings Button */}
          <Tooltip title="Settings">
            <IconButton color="inherit" sx={{ ml: 1 }}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton
              color="inherit"
              onClick={handleNotificationsOpen}
              sx={{ ml: 1 }}
            >
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={notificationsAnchorEl}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleNotificationsClose}
            PaperProps={{
              sx: {
                width: 320,
                maxHeight: 400,
                backgroundColor: 'rgba(30, 35, 45, 0.9)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                color: 'white'
              }
            }}
          >
            <Box p={2}>
              <Typography variant="h6">Notifications</Typography>
            </Box>
            <MenuItem sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  New ticket assigned
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Ticket #TKT-4872 has been assigned to you
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  5 minutes ago
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  @Mention in ticket
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  Sarah mentioned you in ticket #TKT-4870
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  20 minutes ago
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem>
              <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                  Urgent ticket update
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  High priority ticket #TKT-4865 has been updated
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                  1 hour ago
                </Typography>
              </Box>
            </MenuItem>
            <Box p={2} display="flex" justifyContent="center">
              <Button variant="text" color="primary">
                View All Notifications
              </Button>
            </Box>
          </Menu>

          {/* User Profile */}
          <Box sx={{ ml: 2 }}>
            <IconButton
              onClick={handleMenuOpen}
              size="small"
              sx={{ padding: 0 }}
            >
              <Avatar
                alt={user?.name || 'User'}
                src={user?.avatar}
                sx={{
                  width: 36,
                  height: 36,
                  border: '2px solid rgba(255,255,255,0.2)'
                }}
              >
                {user?.name?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  backgroundColor: 'rgba(30, 35, 45, 0.9)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  color: 'white'
                }
              }}
            >
              <MenuItem onClick={handleMenuClose}>My Profile</MenuItem>
              <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
              <MenuItem onClick={handleMenuClose}>Help Center</MenuItem>
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
              <MenuItem onClick={handleMenuClose}>Logout</MenuItem>
            </Menu>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopAppBar;
